import React, { useState, useCallback } from 'react';
import { Button, Card, Space, Typography, Alert, Tooltip, Badge } from 'antd';
import { SyncOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { syncCalls } from '../utils/sipuniApi';

const { Text } = Typography;

/**
 * Компонент для управления синхронизацией звонков Sipuni
 * Позволяет вручную запустить синхронизацию данных о звонках
 */
const CallsSyncManager = ({ 
  dateRange, 
  onSyncComplete, 
  onSyncError,
  compact = false 
}) => {
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState(null);

  // Ручная синхронизация
  const handleSync = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const startDate = dateRange?.startDate || new Date().toISOString().split('T')[0];
      const endDate = dateRange?.endDate || new Date().toISOString().split('T')[0];
      
      const result = await syncCalls(startDate, endDate);
      
      setLastSync(new Date());
      
      if (onSyncComplete) {
        onSyncComplete(result);
      }
      
      return result;
    } catch (err) {
      const errorMsg = err.message || 'Ошибка синхронизации звонков';
      setError(errorMsg);
      
      if (onSyncError) {
        onSyncError(err);
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [dateRange, onSyncComplete, onSyncError]);


  if (compact) {
    return (
      <Space>
        {lastSync && (
          <Tooltip title={`Последняя синхронизация: ${lastSync.toLocaleString('ru-RU')}`}>
            <Badge dot color="green">
              <ClockCircleOutlined style={{ color: '#52c41a' }} />
            </Badge>
          </Tooltip>
        )}
        
        {error && (
          <Tooltip title={error}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          </Tooltip>
        )}
        
        <Button 
          size="small"
          icon={<SyncOutlined />}
          loading={loading}
          onClick={handleSync}
          type="primary"
        >
          Синхронизировать
        </Button>
      </Space>
    );
  }

  return (
    <Card 
      title="Управление звонками Sipuni" 
      size="small"
      extra={
        <Button 
          icon={<SyncOutlined />}
          loading={loading}
          onClick={handleSync}
          type="primary"
        >
          Синхронизировать звонки
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Статус последней синхронизации */}
        {lastSync && (
          <Text type="secondary">
            <ClockCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
            Последняя синхронизация: {lastSync.toLocaleString('ru-RU')}
          </Text>
        )}
        
        {/* Ошибки */}
        {error && (
          <Alert
            message="Ошибка синхронизации"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}
        
        {/* Информация */}
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Синхронизация загружает данные о звонках из Sipuni за выбранный период.
          Данные используются для расчета статистики по менеджерам.
        </Text>
      </Space>
    </Card>
  );
};

export default CallsSyncManager;
